import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

// "Share with brand" on a plan: the team issues one link per plan, the brand
// opens it (no login) and approves/rejects creators. Server side:
// pluginbackendnode routes/plans.js (share-link) + routes/planReview.js (public).

const PLAN_SUMMARY_KEY = (planId) => ["plan-summary", planId];
const REVIEW_KEY = (token) => ["plan-review", token];

export const planReviewUrlFor = (token) => (token ? `${window.location.origin}/plan-review/${token}` : null);

// ---- Team ------------------------------------------------------------------

const useShareLinkMutation = (planId, path) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => axios.post(`/plans/${planId}/${path}`).then((r) => r.data),
    onSuccess: ({ brandShareToken }) => {
      // Patch the token in place — refetching the whole summary for one string is wasteful.
      queryClient.setQueryData(PLAN_SUMMARY_KEY(planId), (prev) =>
        prev ? { ...prev, plan: { ...prev.plan, brandShareToken } } : prev
      );
    },
  });
};

// Idempotent: returns the existing link, or creates it on first share.
export const useCreateBrandShareLink = (planId) => useShareLinkMutation(planId, "share-link");
export const useRotateBrandShareLink = (planId) => useShareLinkMutation(planId, "share-link/rotate");

// Columns the team may show on the brand link, in display order — mirrors
// BRAND_SHAREABLE_COLUMNS in pluginbackendnode/models/plan.js (the server
// enforces it; internal costs/contacts aren't in it on purpose). Keys match
// ALL_COLUMNS in plans/InfluencerTable.jsx, which supplies the labels.
export const BRAND_SHAREABLE_COLUMNS = [
  "followers", "likesAvg", "viewsAvg", "commentsAvg", "sharesAvg", "repostsAvg", "engagementRate",
  "brandCost", "category", "language", "location", "reason", "customFields",
];

export const useSetBrandColumns = (planId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (columns) => axios.patch(`/plans/${planId}/share-link/columns`, { columns }).then((r) => r.data),
    onSuccess: ({ brandVisibleColumns }) =>
      queryClient.setQueryData(PLAN_SUMMARY_KEY(planId), (prev) =>
        prev ? { ...prev, plan: { ...prev.plan, brandVisibleColumns } } : prev
      ),
  });
};

// ---- Brand (public link) -----------------------------------------------------

// A bad/replaced link is a 404 — no point retrying it.
export const usePlanReview = (token) =>
  useQuery({
    queryKey: REVIEW_KEY(token),
    queryFn: () => axios.get(`/plan-review/${token}`).then((r) => r.data),
    enabled: Boolean(token),
    retry: false,
  });

// {influencerIds, decision: "approved"|"rejected"|"pending", note?} — answers
// with the fresh payload, which replaces the cache directly.
export const useBrandDecision = (token, reviewerName) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => axios.post(`/plan-review/${token}/decision`, { ...payload, reviewerName }).then((r) => r.data),
    onSuccess: (data) => queryClient.setQueryData(REVIEW_KEY(token), data),
  });
};
